import { UserPickerMultiSelect } from "@/components/shared/UserPicker";
import { useCaseQcReviewers } from "@/hooks/useCaseQcReviewers";
import type { Analyst } from "@/services/usersService";
import { useMemo } from "react";

interface CaseUser {
  user_id: string;
  name: string;
}

interface QcReviewerBadgesProps {
  caseId: string;
  qcReviewers: CaseUser[];
  caseAnalysts: CaseUser[];
  availableQcReviewers: Analyst[];
  isEditable?: boolean;
}

const QcReviewerBadges = ({
  caseId,
  qcReviewers,
  caseAnalysts,
  availableQcReviewers,
  isEditable = true,
}: QcReviewerBadgesProps) => {
  const { addQcReviewer, removeQcReviewer } = useCaseQcReviewers(caseId);

  const available = useMemo(
    () =>
      availableQcReviewers.filter(
        (a) =>
          !qcReviewers.find((existing) => existing.user_id === a.user_id) &&
          !caseAnalysts.find((analyst) => analyst.user_id === a.user_id)
      ),
    [availableQcReviewers, qcReviewers, caseAnalysts]
  );

  return (
    <UserPickerMultiSelect
      users={available}
      value={qcReviewers}
      onAdd={async (userId) => {
        // Looks up the unfiltered roster: the picker was handed `available`, a subset.
        const reviewer = availableQcReviewers.find((r) => r.user_id === userId);
        if (!reviewer) throw new Error(`QC reviewer ${userId} is not in the candidate list`);
        await addQcReviewer({ user_id: reviewer.user_id, name: reviewer.name });
      }}
      onRemove={async (userId) => {
        await removeQcReviewer(userId);
      }}
      variant="green"
      addLabel="Add QC Reviewer"
      emptyText="No QC reviewers available to add."
      isEditable={isEditable}
    />
  );
};

export default QcReviewerBadges;
